<template>
  <div>
    <div
      v-if="carregando"
      class="text-center py-5"
    >
      <b-spinner variant="primary" />
    </div>

    <template v-else-if="equipamento">
      <b-card
        no-body
        class="mb-1"
      >
        <div class="hist__topo">
          <div>
            <div class="hist__nome">
              {{ equipamento.name }}
            </div>
            <div class="hist__sub">
              {{ subtitulo }}
            </div>
          </div>
          <div class="hist__saldos">
            <div class="hist__saldo">
              <div class="hist__saldo-valor">
                {{ equipamento.total_quantity }}
              </div>
              <div class="hist__saldo-rotulo">
                {{ $t('equipment.history.totalLabel') }}
              </div>
            </div>
            <div class="hist__saldo">
              <div class="hist__saldo-valor">
                {{ equipamento.em_uso }}
              </div>
              <div class="hist__saldo-rotulo">
                {{ $t('equipment.history.inUseLabel') }}
              </div>
            </div>
            <div class="hist__saldo">
              <div
                class="hist__saldo-valor"
                :class="{ 'hist__saldo-valor--alerta': equipamento.retido > 0 }"
              >
                {{ equipamento.retido }}
              </div>
              <div class="hist__saldo-rotulo">
                {{ $t('equipment.history.retainedLabel') }}
              </div>
            </div>
            <div class="hist__saldo">
              <div
                class="hist__saldo-valor"
                :class="equipamento.disponivel === 0 ? 'hist__saldo-valor--perigo' : 'hist__saldo-valor--ok'"
              >
                {{ equipamento.disponivel }}
              </div>
              <div class="hist__saldo-rotulo">
                {{ $t('equipment.history.availableLabel') }}
              </div>
            </div>
          </div>
        </div>
      </b-card>

      <b-card no-body>
        <div class="hist__titulo">
          <feather-icon
            icon="ClockIcon"
            size="15"
            class="mr-50 text-primary"
          />
          {{ $t('equipment.history.trackingTitle') }}
          <span class="ml-auto hist__contagem">{{ $t('equipment.history.eventsCount', { count: paginacao.total }) }}</span>
        </div>

        <div
          v-if="!eventos.length"
          class="text-center text-muted py-4"
        >
          {{ $t('equipment.history.noEvents') }}
        </div>

        <div
          v-else
          class="hist__tl"
        >
          <div
            v-for="(ev, idx) in eventos"
            :key="idx"
            class="tl__item"
          >
            <div class="tl__trilho">
              <div
                class="tl__ponto"
                :style="{ backgroundColor: cor(ev.kind) }"
              />
              <div
                v-if="idx < eventos.length - 1"
                class="tl__conector"
              />
            </div>

            <div class="tl__corpo">
              <div class="tl__linha">
                <span
                  class="tl__rotulo"
                  :style="{ color: cor(ev.kind) }"
                >
                  {{ ev.label }}
                  <template v-if="ev.quantity"> &middot; {{ ev.quantity }}</template>
                </span>
                <span class="tl__data">{{ dataHora(ev.at) }}</span>
              </div>

              <div class="tl__metas">
                <span
                  v-if="ev.team"
                  class="tl__chip"
                >{{ ev.team }}</span>
                <router-link
                  v-if="ev.retention_id"
                  class="tl__chip tl__chip--link"
                  :to="{ name: 'retention-detail', params: { id: ev.retention_id } }"
                >
                  {{ $t('equipment.history.retentionLink', { id: ev.retention_id }) }}
                </router-link>
                <span
                  v-if="ev.protocol"
                  class="tl__chip tl__chip--proto"
                >{{ ev.protocol }}</span>
                <span
                  v-if="ev.total_before !== null && ev.total_after !== null"
                  class="tl__chip"
                >total {{ ev.total_before }} &rarr; {{ ev.total_after }}</span>
              </div>

              <div
                v-if="ev.user"
                class="tl__meta"
              >
                <feather-icon
                  icon="UserIcon"
                  size="11"
                  class="mr-25"
                />{{ ev.user }}
              </div>
              <div
                v-if="ev.notes"
                class="tl__obs"
              >
                "{{ ev.notes }}"
              </div>
            </div>
          </div>
        </div>

        <div
          v-if="paginacao.last_page > 1"
          class="d-flex justify-content-center p-1 hist__paginacao"
        >
          <pagination
          :options="opcoesPaginacao"
            v-model="paginacao.current_page"
            :records="paginacao.total"
            :per-page="paginacao.per_page"
            @paginate="carregar"
          />
        </div>
      </b-card>
    </template>

    <div
      v-else
      class="text-center text-muted py-5"
    >
      {{ $t('equipment.history.notFound') }}
    </div>
  </div>
</template>

<script>
import { BSpinner } from 'bootstrap-vue'
import Pagination from 'vue-pagination-2'
import opcoesPaginacao from '@/libs/paginationOptions'
import ApiErrorMixin from '@/mixins/ApiErrorMixin'

const CORES = {
  cadastro: '#7367f0',
  entrada: '#28c76f',
  saida: '#ff9f43',
  alocacao: '#00cfe8',
  devolucao: '#8592a3',
  retencao: '#ff9f43',
  recuperacao: '#28c76f',
  contato: '#7367f0',
  prazo: '#7367f0',
  sem_resposta: '#ea5455',
  extraviado: '#ea5455',
  nota: '#8592a3',
  edicao: '#8592a3',
}

export default {
  name: 'EquipmentHistory',
  components: { BSpinner, Pagination },
  mixins: [ApiErrorMixin],

  data() {
    return {
      equipamento: null,
      eventos: [],
      paginacao: {
        current_page: 1, per_page: 25, last_page: 1, total: 0,
      },
      carregando: false,
    }
  },

  computed: {
    opcoesPaginacao() {
      return opcoesPaginacao
    },

    subtitulo() {
      const e = this.equipamento
      if (!e) return ''

      const partes = []
      if (e.measure_value) partes.push(`${e.measure_value} ${e.unit || ''}`.trim())
      if (e.serial) partes.push(e.serial)
      if (e.type) partes.push(e.type)

      return partes.join(' · ')
    },
  },

  mounted() {
    this.carregar()
  },

  methods: {
    async carregar() {
      this.carregando = true
      try {
        const res = await this.$http.get(
          `${this.$store.state.api}equipment/${this.$route.params.id}/history`,
          { params: { page: this.paginacao.current_page, per_page: this.paginacao.per_page } },
        )
        const dados = res.data.data || {}
        const pg = dados.events || {}
        this.equipamento = dados.equipment || null
        this.eventos = pg.data || []
        this.paginacao.total = pg.total || 0
        this.paginacao.last_page = pg.last_page || 1
        this.paginacao.current_page = pg.current_page || 1
      } catch (e) {
        this.equipamento = null
        this.$message(this.$t('equipment.common.errorTitle'), this.apiErrorText(e, this.$t('equipment.history.loadError')), 'error')
      } finally {
        this.carregando = false
      }
    },

    cor(kind) {
      return CORES[kind] || '#8592a3'
    },

    dataHora(iso) {
      if (!iso) return '—'
      return new Date(iso).toLocaleString('pt-BR')
    },
  },
}
</script>

<style scoped>
.hist__topo {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 16px;
  flex-wrap: wrap;
  padding: 16px 18px;
}

.hist__nome {
  font-size: 17px;
  font-weight: 600;
  color: #5e5873;
  line-height: 1.25;
}

.hist__sub {
  font-size: 12.5px;
  color: #8592a3;
  margin-top: 2px;
}

.hist__saldos {
  display: flex;
  gap: 26px;
}

.hist__saldo-valor {
  font-size: 1.3rem;
  font-weight: 700;
  line-height: 1.1;
  font-variant-numeric: tabular-nums;
  color: #5e5873;
}

.hist__saldo-valor--alerta {
  color: #ff9f43;
}

.hist__saldo-valor--ok {
  color: #28c76f;
}

.hist__saldo-valor--perigo {
  color: #ea5455;
}

.hist__saldo-rotulo {
  font-size: 10.5px;
  font-weight: 600;
  letter-spacing: 0.05em;
  text-transform: uppercase;
  color: #b9b9c3;
  margin-top: 2px;
}

.hist__titulo {
  display: flex;
  align-items: center;
  padding: 14px 18px;
  border-bottom: 1px solid #ebe9f1;
  font-weight: 600;
  font-size: 14px;
  color: #5e5873;
}

.hist__contagem {
  font-size: 11.5px;
  font-weight: 400;
  color: #b9b9c3;
}

.hist__tl {
  padding: 16px 18px;
}

.hist__paginacao {
  border-top: 1px solid #ebe9f1;
}

.dark-layout .hist__paginacao {
  border-top-color: #3b4253;
}

.tl__item {
  display: flex;
  gap: 12px;
}

.tl__trilho {
  display: flex;
  flex-direction: column;
  align-items: center;
  flex: 0 0 10px;
}

.tl__ponto {
  width: 10px;
  height: 10px;
  border-radius: 50%;
  margin-top: 5px;
  flex: none;
}

.tl__conector {
  flex: 1 1 auto;
  border-left: 2px solid #ebe9f1;
  margin: 3px 0;
}

.tl__corpo {
  flex: 1 1 auto;
  min-width: 0;
  padding-bottom: 16px;
}

.tl__linha {
  display: flex;
  align-items: baseline;
  justify-content: space-between;
  gap: 8px;
}

.tl__rotulo {
  font-weight: 600;
  font-size: 13.5px;
}

.tl__data {
  font-size: 11px;
  color: #b9b9c3;
  white-space: nowrap;
}

.tl__metas {
  display: flex;
  flex-wrap: wrap;
  gap: 5px;
  margin-top: 4px;
}

.tl__chip {
  font-size: 10.5px;
  font-weight: 600;
  padding: 2px 7px;
  border-radius: 10px;
  background: #f3f2f7;
  color: #6e6b7b;
}

.tl__chip--link {
  color: #7367f0;
}

.tl__chip--proto {
  font-family: monospace;
}

.tl__meta {
  font-size: 11.5px;
  color: #8592a3;
  margin-top: 3px;
}

.tl__obs {
  font-size: 11.5px;
  font-style: italic;
  color: #6e6b7b;
  margin-top: 3px;
}

/* dark mode */
.dark-layout .hist__titulo {
  border-bottom-color: #3b4253;
}

.dark-layout .hist__nome,
.dark-layout .hist__saldo-valor,
.dark-layout .hist__titulo {
  color: #d0d2d6;
}

.dark-layout .hist__saldo-rotulo {
  color: #676d7d;
}

.dark-layout .tl__conector {
  border-left-color: #3b4253;
}

.dark-layout .tl__chip {
  background: #343d55;
  color: #b4b7bd;
}

.dark-layout .tl__obs {
  color: #b4b7bd;
}
</style>
